import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Add to cart feedback (helpdesk ticket HD-2236)
 *
 * Gives customers a clear next step after clicking "Add to cart":
 *
 * 1. Split button: the add to cart button splits into "Add" + "Go to cart".
 *    Markup: snippets/add-to-cart-button.liquid (only buttons with `data-split-button` take part).
 * 2. Notification: an "Added to cart" toast with the product image, title, a cart link and a progress bar.
 *    Markup: snippets/add-to-cart-feedback.liquid (rendered once in layout/theme.liquid).
 *
 * Both react to the CartAddEvent that the theme's product form already dispatches after a
 * successful add, so the existing add to cart logic in product-form.js is not changed.
 */

/** The product form tags its add to cart events with this source. */
const PRODUCT_FORM_SOURCE = 'product-form-component';

/** Width of the product image in the toast, at 2x for sharp images on retina screens. */
const TOAST_IMAGE_WIDTH = 128;

/**
 * @typedef {object} CartItem
 * @property {number} product_id
 * @property {number} variant_id
 * @property {string} product_title
 * @property {string | null} image
 */

/**
 * True for a successful add from a product form.
 * Ignores failed adds and other cart updates (cart page changes, the free membership product, quick order list).
 *
 * @param {Event} event
 * @returns {event is CartAddEvent}
 */
function isSuccessfulProductAdd(event) {
  return (
    event instanceof CartAddEvent &&
    event.detail?.data?.source === PRODUCT_FORM_SOURCE &&
    !event.detail.data.didError
  );
}

/**
 * Splits the add to cart button(s) of the added product into "Add" + "Go to cart".
 * The CSS in add-to-cart-feedback.css takes care of the layout.
 *
 * @param {string} productId
 */
function splitAddToCartButtons(productId) {
  const selector = `product-form-component[data-product-id="${CSS.escape(productId)}"] add-to-cart-component[data-split-button]`;

  for (const button of document.querySelectorAll(selector)) {
    if (button instanceof HTMLElement) button.dataset.split = 'true';
  }
}

/**
 * Fetches the cart and returns the line of the product that was just added.
 * The cart lists the newest line first, so the first match is the added one.
 *
 * @param {string} productId
 * @param {string} variantId
 * @returns {Promise<CartItem | null>}
 */
async function fetchAddedCartItem(productId, variantId) {
  try {
    // Theme.routes.cart_url includes the language prefix (e.g. /nl/cart), so titles come back translated.
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;

    /** @type {{ items?: CartItem[] }} */
    const cart = await response.json();
    const items = cart.items ?? [];

    return (
      items.find((item) => String(item.variant_id) === variantId) ??
      items.find((item) => String(item.product_id) === productId) ??
      null
    );
  } catch (error) {
    console.error('Add to cart feedback: could not load the cart', error);
    return null;
  }
}

/**
 * Returns the image URL resized for the toast.
 *
 * @param {string} src
 * @returns {string}
 */
function getToastImageUrl(src) {
  const url = new URL(src, window.location.origin);
  url.searchParams.set('width', String(TOAST_IMAGE_WIDTH));
  return url.toString();
}

/**
 * "Added to cart" notification.
 *
 * - Opens after every add and closes itself after 6 seconds, when the progress bar runs out.
 * - The timer pauses while the customer hovers or tabs into the toast (CSS pauses the progress bar).
 * - Can be closed with the close button or the Escape key.
 * - Adding again while it is open restarts it with the latest product.
 *
 * @typedef {object} CartAddedToastRefs
 * @property {HTMLElement} status - Screen reader announcement.
 * @property {HTMLElement} panel - The visible toast.
 * @property {HTMLElement} heading - "Added to cart" heading.
 * @property {HTMLElement} product - Product row (image + title).
 * @property {HTMLImageElement} image - Product image.
 * @property {HTMLElement} title - Product title.
 * @property {HTMLElement} progress - Progress bar that acts as the timer.
 *
 * @extends {Component<CartAddedToastRefs>}
 */
class CartAddedToast extends Component {
  requiredRefs = ['status', 'panel', 'heading', 'product', 'image', 'title', 'progress'];

  /** Increases with every add, so a slow cart request cannot overwrite a newer one. */
  #latestRequest = 0;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // The progress bar is the timer: when its animation ends, the toast closes.
    this.refs.progress.addEventListener('animationend', this.#handleProgressEnd, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Shows the toast for the product that was just added.
   *
   * @param {string} productId
   * @param {string} variantId
   */
  async show(productId, variantId) {
    const request = ++this.#latestRequest;
    const item = await fetchAddedCartItem(productId, variantId);

    // The customer added something else in the meantime: that newer add wins.
    if (request !== this.#latestRequest) return;

    this.#render(item);
    this.#open();
  }

  /** Closes the toast. Used by the close button (on:click="/close"). */
  close = () => {
    this.dataset.open = 'false';
    this.refs.panel.inert = true;
  };

  /**
   * Closes the toast with the Escape key (on:keydown="/handleKeydown").
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  /**
   * Fills the toast with the image and title of the added product. Without product data
   * (cart request failed) it still shows the heading and the cart link.
   *
   * @param {CartItem | null} item
   */
  #render(item) {
    const { product, image, title, heading, status } = this.refs;

    product.hidden = !item;
    title.textContent = item?.product_title ?? '';

    if (item?.image) {
      image.src = getToastImageUrl(item.image);
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.hidden = true;
    }

    // Announce e.g. "Ajouté au panier: SPA® Fontaine compacte" to screen readers.
    const headingText = heading.textContent?.trim() ?? '';
    status.textContent = item ? `${headingText}: ${item.product_title}` : headingText;
  }

  /** Opens the toast (or restarts it when it is already open). */
  #open() {
    this.#updateHeaderOffset();

    this.refs.panel.inert = false;
    this.dataset.open = 'true';

    // Restart the progress bar so every add gets the full display time.
    const { progress } = this.refs;
    progress.style.animation = 'none';
    void progress.offsetWidth; // Force a reflow so the browser picks up the restart.
    progress.style.animation = '';
  }

  /**
   * Places the toast just below the header when the header is visible,
   * so it never covers the menu or the cart icon.
   */
  #updateHeaderOffset() {
    const header = document.getElementById('header-component');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

    this.style.setProperty('--cart-toast-header-offset', `${Math.round(headerBottom)}px`);
  }

  /** @param {AnimationEvent} event */
  #handleProgressEnd = (event) => {
    if (event.target === this.refs.progress) this.close();
  };
}

if (!customElements.get('cart-added-toast')) {
  customElements.define('cart-added-toast', CartAddedToast);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (!isSuccessfulProductAdd(event)) return;

  const productId = String(event.detail.data.productId ?? '');
  const variantId = String(event.detail.sourceId ?? '');

  if (productId) splitAddToCartButtons(productId);

  const toast = document.querySelector('cart-added-toast');
  if (toast instanceof CartAddedToast) toast.show(productId, variantId);
});
